import React from "react";
import { Input, Select, DatePicker, InputNumber, Checkbox, Row, Col, Empty, Alert } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// Components
import SectionCard from "../../../../components/shared/SectionCard";

// Types
import { Test, FormField, normalizeTestType } from "../../../../types/healthcare/TestTypes";

// Utils
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { TextArea } = Input;

interface DynamicFormFieldsProps {
	currentOrder: Partial<Test>;
	formFields: FormField[];
	isNotLocked: boolean;
	handleDetailChange: (fieldName: string, value: any) => void;
}

const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({ currentOrder, formFields, isNotLocked, handleDetailChange }) => {
	const dateFormat = loadUserPreferences().dateFormat.toUpperCase();
	const selectedTestTypes = normalizeTestType(currentOrder?.test_type);

	const renderFormField = (field: FormField) => {
		const fieldValue = currentOrder?.details?.[field.name];

		switch (field.type) {
			case "text":
				return <Input size="small" value={fieldValue || ""} onChange={(e) => handleDetailChange(field.name, e.target.value)} placeholder={field.placeholder} disabled={!isNotLocked} />;

			case "textarea":
				return <TextArea value={fieldValue || ""} onChange={(e) => handleDetailChange(field.name, e.target.value)} rows={2} placeholder={field.placeholder} disabled={!isNotLocked} />;

			case "date":
				return <DatePicker size="small" value={fieldValue ? dayjs(fieldValue) : null} onChange={(date) => handleDetailChange(field.name, date?.toISOString())} format={dateFormat} style={{ width: "100%" }} disabled={!isNotLocked} />;

			case "number":
				return <InputNumber size="small" value={fieldValue || null} onChange={(value) => handleDetailChange(field.name, value)} placeholder={field.placeholder} style={{ width: "100%" }} disabled={!isNotLocked} />;

			case "boolean":
				return (
					<Checkbox checked={fieldValue || false} onChange={(e) => handleDetailChange(field.name, e.target.checked)} disabled={!isNotLocked}>
						{field.description || "Yes"}
					</Checkbox>
				);

			case "dropdown":
				if (field.options) {
					return (
						<Select
							size="small"
							value={fieldValue || null}
							options={field.options.map((opt) => ({
								label: typeof opt === "string" ? opt : opt.label,
								value: typeof opt === "string" ? opt : opt.value,
							}))}
							onChange={(value) => handleDetailChange(field.name, value)}
							placeholder={field.placeholder || "Select"}
							style={{ width: "100%" }}
							showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
							disabled={!isNotLocked}
						/>
					);
				}
				return null;

			case "checkbox":
				if (field.options) {
					return (
						<Select
							mode="multiple"
							size="small"
							value={fieldValue || []}
							options={field.options.map((opt) => ({
								label: typeof opt === "string" ? opt : opt.label,
								value: typeof opt === "string" ? opt : opt.value,
							}))}
							onChange={(value) => handleDetailChange(field.name, value)}
							placeholder={field.placeholder || "Select multiple"}
							style={{ width: "100%" }}
							disabled={!isNotLocked}
						/>
					);
				}
				return null;

			default:
				return <Input size="small" value={fieldValue || ""} onChange={(e) => handleDetailChange(field.name, e.target.value)} disabled={!isNotLocked} />;
		}
	};

	if (selectedTestTypes.length === 0) {
		return null;
	}

	if (!formFields || formFields.length === 0) {
		return null;
	}

	return (
		<SectionCard title="Additional Information">
			{selectedTestTypes.length > 1 && <Alert message="Multiple Test Types Selected" description={`Form fields are based on the primary test type: ${selectedTestTypes[0]}`} type="info" showIcon style={{ marginBottom: 8 }} />}

			{formFields.length === 0 ? (
				<Empty image={<FileTextOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />} description="No additional fields required for this test type" />
			) : (
				<Row gutter={[12, 8]}>
					{formFields.map((field) => (
						<Col xs={24} md={12} key={field.name}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontWeight: 500, fontSize: 11 }}>
									{field.label}
									{field.required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
								</label>
								{renderFormField(field)}
								{field.description && field.type !== "boolean" && <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>{field.description}</div>}
							</div>
						</Col>
					))}
				</Row>
			)}
		</SectionCard>
	);
};

export default DynamicFormFields;
